import { saveAs } from 'file-saver';
import { LabelItem } from '../types/LabelItem';
import QRCode from 'qrcode';
import { generateQRCodeContent } from '../utils/qrCodeGenerator';

/**
 * Service for generating Word documents with QR code labels.
 *
 * Uses the Ettikettmall.docx template from assets and fills it with
 * QR codes and user/storage location data.
 */
class WordDocumentService {
    /**
     * Generates QR code as base64 data URL
     */
    private async generateQRCodeDataUrl(data: string): Promise<string> {
        try {
            // Generate QR code as data URL (base64)
            // 2cm = ~56.7 points in Word, we generate higher resolution for quality
            const qrDataUrl = await QRCode.toDataURL(data, {
                errorCorrectionLevel: 'H',
                width: 150, // Higher resolution for print quality
                margin: 0, // No margin, we control spacing in CSS
            });
            return qrDataUrl;
        } catch (error) {
            console.error('Error generating QR code:', error);
            throw error;
        }
    }

    /**
     * Generates a Word document with labels starting at a specific position.
     *
     * @param items - Label items to generate
     * @param startPosition - Starting cell position (0-23)
     * @returns Promise that resolves when document is downloaded
     */
    async generateDocument(
        items: LabelItem[],
        startPosition: number = 0
    ): Promise<void> {
        try {
            // Generate labels array with padding for start position
            const paddedItems: (LabelItem | null)[] = [
                ...Array(startPosition).fill(null),
                ...items,
            ];

            // Create HTML table that matches the 3x8 grid
            let html = `
                <html xmlns:o='urn:schemas-microsoft-com:office:office'
                      xmlns:w='urn:schemas-microsoft-com:office:word'
                      xmlns='http://www.w3.org/TR/REC-html40'>
                <head>
                    <meta charset='utf-8'>
                    <title>QR Code Labels</title>
                    <!--[if gte mso 9]>
                    <xml>
                        <w:WordDocument>
                            <w:View>Print</w:View>
                            <w:Zoom>100</w:Zoom>
                        </w:WordDocument>
                        <w:LatentStyles DefLockedState="false" DefUnhideWhenUsed="false"
                            DefSemiHidden="false" DefQFormat="false" DefPriority="99"
                            LatentStyleCount="376">
                        </w:LatentStyles>
                    </xml>
                    <![endif]-->
                    <style>
                        @page Section1 {
                            size: 21.0cm 29.7cm;
                            margin: 0cm 0cm 0cm 0cm;
                            mso-header-margin: 0cm;
                            mso-footer-margin: 0cm;
                            mso-paper-source: 0;
                        }
                        div.Section1 {
                            page: Section1;
                        }
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        body {
                            margin: 0;
                            padding: 0;
                            font-family: Arial, sans-serif;
                        }
                        table {
                            width: 21cm;
                            border-collapse: collapse;
                            table-layout: fixed;
                            margin: 0;
                            padding: 0;
                            border-spacing: 0;
                        }
                        td {
                            width: 7cm;
                            height: 3.69cm;
                            padding: 0;
                            text-align: center;
                            vertical-align: middle;
                            border: none;
                            margin: 0;
                        }
                        .qr-codes {
                            text-align: center;
                            margin: 0;
                            padding: 0;
                            line-height: 0;
                        }
                        .qr-codes img {
                            width: 2cm !important;
                            height: 2cm !important;
                            max-width: 2cm !important;
                            max-height: 2cm !important;
                            display: inline-block;
                            margin: 0 0.4cm;
                            padding: 0;
                            vertical-align: middle;
                        }
                        .label-text {
                            font-size: 9pt;
                            font-weight: bold;
                            text-align: center;
                            margin: 0.3cm 0 0 0;
                            padding: 0;
                            line-height: 1.2;
                        }
                        .qr-codes-storage {
                            text-align: center;
                            margin: 0;
                            padding: 0;
                            line-height: 0;
                        }
                        .qr-codes-storage img {
                            width: 3cm !important;
                            height: 3cm !important;
                            max-width: 3cm !important;
                            max-height: 3cm !important;
                            display: inline-block;
                            margin: 0;
                            padding: 0;
                            vertical-align: middle;
                        }
                        .label-text-storage {
                            font-size: 9pt;
                            font-weight: bold;
                            text-align: center;
                            margin: 0;
                            padding: 0;
                            line-height: 1.2;
                        }
                        .page-break {
                            page-break-after: always;
                        }
                    </style>
                </head>
                <body style='margin:0;padding:0'>
                <div class='Section1'>
            `;

            // Generate sheets
            const LABELS_PER_SHEET = 24;
            const totalSheets = Math.ceil(
                paddedItems.length / LABELS_PER_SHEET
            );

            for (let sheetIndex = 0; sheetIndex < totalSheets; sheetIndex++) {
                const sheetStart = sheetIndex * LABELS_PER_SHEET;
                const sheetEnd = sheetStart + LABELS_PER_SHEET;
                const sheetItems = paddedItems.slice(sheetStart, sheetEnd);

                // Check if this sheet has any actual items (not all nulls)
                const hasContent = sheetItems.some((item) => item !== null);
                if (!hasContent) {
                    continue; // Skip empty sheets
                }

                // Pad to 24 cells
                while (sheetItems.length < LABELS_PER_SHEET) {
                    sheetItems.push(null);
                }

                html += `<table${sheetIndex < totalSheets - 1 ? ' class="page-break"' : ''}>`;

                // Generate 8 rows
                for (let row = 0; row < 8; row++) {
                    html += '<tr>';

                    // Generate 3 columns
                    for (let col = 0; col < 3; col++) {
                        const cellIndex = row * 3 + col;
                        const item = sheetItems[cellIndex];

                        html += '<td>';

                        if (item) {
                            // Generate QR codes
                            const qrContent = generateQRCodeContent(
                                item.qrData
                            );
                            const qrDataUrl =
                                await this.generateQRCodeDataUrl(qrContent);

                            console.log(
                                'Item type:',
                                item.type,
                                'Display name:',
                                item.displayName
                            );

                            if (item.type === 'storage') {
                                // Storage: 1 QR code, 3x3cm, no padding
                                html += `
                                    <div class="qr-codes-storage">
                                        <img src="${qrDataUrl}" width="85.05" height="85.05" alt="QR" />
                                    </div>
                                    <div class="label-text-storage">${this.escapeHtml(item.displayName)}</div>
                                `;
                            } else {
                                // User: 2 QR codes, 2x2cm each
                                html += `
                                    <div class="qr-codes">
                                        <img src="${qrDataUrl}" width="56.7" height="56.7" alt="QR 1" />&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<img src="${qrDataUrl}" width="56.7" height="56.7" alt="QR 2" />
                                    </div>
                                    <div class="label-text">${this.escapeHtml(item.displayName)}</div>
                                `;
                            }
                        }

                        html += '</td>';
                    }

                    html += '</tr>';
                }

                html += '</table>';
            }

            html += '</div></body></html>';

            // Create blob and download
            const blob = new Blob([html], {
                type: 'application/vnd.ms-word',
            });

            const filename = `qr-labels-${new Date().toISOString().split('T')[0]}.doc`;
            saveAs(blob, filename);
        } catch (error) {
            console.error('Error generating document:', error);
            throw error;
        }
    }

    /**
     * Escapes HTML special characters
     */
    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

export default WordDocumentService;
